import { Type } from '@angular/core';

/**
 * Artigo da central de documentação. Cada tema declara as permissões
 * `module:action` que o usuário precisa possuir (TODAS — via `canAll`) para
 * acessá-lo, e o componente (lazy) que renderiza seu conteúdo. Adicionar um novo
 * tema = acrescentar uma entrada aqui + o componente de corpo correspondente; a
 * central e o roteamento são genéricos e não precisam mudar.
 */
export interface DocArticle {
  /** Segmento da URL (`/documentacao/<slug>`) e identidade do artigo. */
  readonly slug: string;
  readonly title: string;
  readonly summary: string;
  readonly icon: string;
  /** Permissões exigidas (TODAS). Ausente/vazio = visível a qualquer autenticado. */
  readonly permissions?: readonly string[];
  /** Carrega (lazy) o componente que renderiza o corpo do artigo. */
  readonly load: () => Promise<Type<unknown>>;
}

/**
 * Catálogo de documentação. Fonte única da central (`/documentacao`) e do gating
 * de cada tema. Mantenha em ordem de exibição.
 */
export const DOC_ARTICLES: readonly DocArticle[] = [
  {
    slug: 'primeiros-passos',
    title: 'Primeiros passos',
    summary: 'Visão geral da plataforma: navegação, ferramentas, aparência, conta e segurança.',
    icon: '✦',
    load: () => import('../../pages/docs/articles/getting-started/getting-started').then((m) => m.GettingStartedDoc),
  },
  {
    slug: 'gestao-de-acessos',
    title: 'Gestão de acessos',
    summary:
      'Como cadastrar, editar e excluir usuários, conceder permissões pela matriz, atribuir bundles e consultar a auditoria.',
    icon: '◍',
    permissions: ['usuarios:read', 'permissoes:read'],
    load: () =>
      import('../../pages/docs/articles/access-management/access-management').then((m) => m.AccessManagementDoc),
  },
  {
    slug: 'consulta-cnpj',
    title: 'Consulta de CNPJ',
    summary:
      'Consultar um CNPJ na hora ou uma lista inteira em lote, acompanhar o progresso e baixar o Excel — e por que o lote demora.',
    icon: '◎',
    permissions: ['cnpj:read'],
    load: () =>
      import('../../pages/docs/articles/consulta-cnpj/consulta-cnpj-doc').then((m) => m.ConsultaCnpjDoc),
  },
  {
    slug: 'simulador-relatorios',
    title: 'Simulador: relatórios e limites',
    summary:
      'Exportar e baixar relatórios, a cota por usuário e — para administradores — o painel de uso e a configuração de limites.',
    icon: '▦',
    permissions: ['simulador:read'],
    load: () =>
      import('../../pages/docs/articles/simulador-relatorios/simulador-relatorios').then((m) => m.SimuladorRelatoriosDoc),
  },
  {
    slug: 'backbone-rtc',
    title: 'Backbone RTC: guia do consultor',
    summary:
      'O método (9 pilares, 69 entregas, F0–F5), setup do projeto, board e checklist, visões, evidências, termos com aceite e o OPR mensal.',
    icon: '◆',
    permissions: ['rtc:execute'],
    load: () => import('../../pages/docs/articles/backbone-rtc/backbone-rtc').then((m) => m.BackboneRtcDoc),
  },
  {
    slug: 'backbone-rtc-cliente',
    title: 'Backbone RTC: guia do cliente',
    summary:
      'Como acompanhar o projeto, marcar o checklist, aceitar termos eletronicamente e baixar documentos e relatórios.',
    icon: '◇',
    permissions: ['rtc:read'],
    load: () =>
      import('../../pages/docs/articles/backbone-rtc-cliente/backbone-rtc-cliente').then((m) => m.BackboneRtcClienteDoc),
  },
];

/** Devolve o artigo do `slug` (ou `undefined` se não existir). */
export function findArticle(slug: string | undefined): DocArticle | undefined {
  return DOC_ARTICLES.find((article) => article.slug === slug);
}
